<template>
  <div class="px-6 pb-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
    <div class="text-[#414141] font-sans">
      <div class="sticky-header">
        <h3 class="text-[1.25rem] text-[#3E02A0] leading-[1.75rem] mb-[1rem] font-semibold">{{ programData?.program_name || 'Program Expenses' }}</h3>
      
        <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
          <ActivityIndicator />
        </div>

        <div v-else-if="error" class="text-red-500 mb-4">
          {{ error }}
        </div>

        <div v-else>
          <div v-if="programData">
            <!-- Tabs -->
            <div class="sticky-tabs">
              <div class="flex space-x-4 border-b border-gray-200">
                <button
                  v-for="(tab, index) in tabs"
                  :key="index"
                  @click="handleTabChange(index)"
                  :class="[
                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5',
                    activeTab === index
                      ? 'border-[#00423D] text-[#00423D]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  ]"
                >
                  {{ tab.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Content Container -->
      <div v-if="programData" class="tab-content">
        <!-- Budget Table Tab -->
        <div v-if="activeTab === 0">
          <div class="mb-8 mt-8">
            <h4 class="text-lg font-semibold text-[#00423D] mb-4">Program Expenses</h4>
            <p class="text-[14px] leading-[22px] mb-[1rem] text-[#414141] font-sans">Under each category below, select the 'Add More' button to itemize your expenses.</p>

            <BudgetTable
              :categories="categories"
              :total-budget="total_programs_budget"
              :budget-total-label="'PROGRAM BUDGET TOTAL'"
              @delete-item="handleDeleteItem"
              @data-changed="handleDataChanged"
            />
            <div v-if="validationError" class="mt-4 text-red-500 text-sm" ref="errorMessageRef">
              {{ validationError }}
            </div>
          </div>
        </div>

        <!-- Budget Justification Tab -->
        <div v-else-if="activeTab === 1">
          <div class="mt-8">
            <h4 class="text-lg font-semibold text-[#00423D] mb-4">Budget Justification</h4>
            <p class="text-[14px] leading-[22px] mb-[1rem] text-[#414141] font-sans">Please provide an explanation for why each of the line item expenses is necessary in achieving your program goal.</p>

            <CoreProgramBudgetJustification
              :program-id="programData.id"
              ref="budgetJustificationComponent"
            />
          </div>
        </div>

        <!-- Program Questions Tab -->
        <div v-else-if="activeTab === 2">
          <div class="mt-8">
            <h4 class="text-lg font-semibold text-[#00423D] mb-4">Program Details</h4>
            <CoreProgramQuestions
              :program-id="programData.id"
              ref="questionsComponent"
            />
          </div>
        </div>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        Program not found. Please check the program ID.
      </div>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to delete this item?</p>
        <div class="flex justify-end space-x-4">
          <button
            @click="conformationPopupIsOpen = false"
            class="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            @click="handleConfirmDelete"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useStepValidation } from '@/composables/useStepValidation';
import BudgetTable from '@/components/BudgetTable.vue';
import CoreProgramQuestions from '@/components/CoreProgramQuestions.vue';
import CoreProgramBudgetJustification from '@/components/CoreProgramBudgetJustification.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useFetchCache } from '@/composables/fetchCache';
import { useFetch } from '@/composables/fetch';
import { usePostAnswer } from '@/composables/postAnswer';
import global from "@/global";

const route = useRoute();
const authStore = useAuthStore();

// Use the validation composable with default settings
useStepValidation();

const isLoading = ref(true);
const error = ref('');
const total_programs_budget = ref(0);
const conformationPopupIsOpen = ref(false);
const deletionFunction = ref(null);
const categories = ref([]);
const programData = ref(null);
const isFetching = ref(false);
const isSaving = ref(false);
const lastSavePromise = ref(null);

// Add validation state tracking
const isExpensesTabValid = ref(false);

// Add validation error ref
const validationError = ref('');

// Add validation state for each tab
const tabValidationState = ref({
  0: false, // Expenses tab
  1: false, // Budget Justification tab
  2: false  // Goals & Rationale tab
});

// Add auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 2000; // 2 seconds delay

const EXPENSE_TYPES = [
  'Personnel and Benefits',
  'Professional and Contractual Fees',
  'Printing and Publications',
  'Travel, Conferences, and Meetings',
  'Marketing and Communications',
  'Facilities',
  'Other Operating Expenses',
  'In-Kind Goods & Services'
];

// Add this mapping at the top, after EXPENSE_TYPES
const API_CATEGORY_KEY_MAP = {
  'Personnel and Benefits': 'personnel_and_benefits',
  'Professional and Contractual Fees': 'professional_and_contractual_fees',
  'Printing and Publications': 'printing_and_publications',
  'Travel, Conferences, and Meetings': 'travel_conferences_and_meetings',
  'Marketing and Communications': 'marketing_and_communications',
  'Facilities': 'facilities',
  'Other Operating Expenses': 'other_expenses',
  'In-Kind Goods & Services': 'in-kind_goods_services',
  'Programs': 'programs',
};

const API_CATEGORY_DISPLAY_MAP = Object.fromEntries(
  Object.entries(API_CATEGORY_KEY_MAP).map(([display, key]) => [key, display])
);

// Mapping for saving expense types (display name -> save name)
// This allows us to show "Other Operating Expenses" but save as "Other Expenses"
const EXPENSE_TYPE_SAVE_MAP = {
  'Other Operating Expenses': 'Other Expenses'
};

// Helper function to get the expense type for saving
const getExpenseTypeForSave = (displayName) => {
  return EXPENSE_TYPE_SAVE_MAP[displayName] || displayName;
};

// Get program slug from route
const programSlug = computed(() => route.params.programSlug);

// Initialize categories
const initializeCategories = () => {
  categories.value = EXPENSE_TYPES.map(type => ({
    name: type,
    isOpen: type === 'Personnel and Benefits',
    items: []
  }));
};

// Fetch program data
const fetchProgramData = async () => {
  try {
    // First try to get from localStorage
    const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
    let program = corePrograms.find(p => 
      p.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === programSlug.value
    );
    
    // If not found in localStorage, fetch from API
    if (!program) {
      const response = await fetch(`${global.API_URL}/core-programs`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const programs = data.programs || [];
      
      // Update localStorage with fresh data
      localStorage.setItem('corePrograms', JSON.stringify(programs));
      
      // Find the program in the fresh data
      program = programs.find(p => 
        p.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === programSlug.value
      );
    }
    
    if (!program) {
      throw new Error('Program not found');
    }
    
    programData.value = program;
  } catch (error) {
    console.error('Error fetching program data:', error);
    throw error;
  }
};

// Watch for route changes to update program data
watch(() => route.params.programSlug, async (newSlug, oldSlug) => {
  if (newSlug && newSlug !== oldSlug) {
    try {
      // Clear any pending auto-save
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
      
      // Reset all state when switching programs
      isLoading.value = true;
      error.value = '';
      validationError.value = '';
      total_programs_budget.value = 0;
      conformationPopupIsOpen.value = false;
      deletionFunction.value = null;
      isFetching.value = false;
      isSaving.value = false;
      lastSavePromise.value = null;
      
      // Reset validation states
      tabValidationState.value = {
        0: false,
        1: false,
        2: false
      };
      
      // Reset active tab to expenses tab
      activeTab.value = 0;
      
      // Clear categories completely
      categories.value = [];
      
      await fetchProgramData();
      await fetchExpenses();
    } catch (error) {
      console.error('Error updating program data:', error);
      error.value = 'Failed to load program data. Please try again.';
    } finally {
      isLoading.value = false;
    }
  }
}, { immediate: true });

// Fetch expenses for the program
const fetchExpenses = async () => {
  if (isFetching.value) return;
  
  try {
    isFetching.value = true;
    const response = await fetch(`${global.API_URL}/expenses?program_id=${programData.value.id}`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    
    // Reset categories completely - don't preserve previous program's state
    initializeCategories();
    
    // Map expenses to categories
    categories.value = EXPENSE_TYPES.map(type => {
      const categoryKey = API_CATEGORY_KEY_MAP[type];
      const categoryExpenses = data.expenses[categoryKey] || [];
      
      // Convert API expenses to items
      const apiItems = categoryExpenses.map(expense => ({
        id: expense.id,
        name: expense.expense_description,
        year1: Number(expense.expense_cost),
        year2: Number(expense.expense_cost_year_2),
        year3: Number(expense.expense_cost_year_3)
      }));
      
      return {
        name: type,
        isOpen: type === 'Personnel and Benefits', // Always start with Personnel and Benefits open
        items: apiItems
      };
    });
    
    // Calculate total budget
    total_programs_budget.value = data.expenses ? 
      Object.values(data.expenses).flat().reduce((sum, expense) => 
        sum + Number(expense.expense_cost), 0) : 0;
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  } finally {
    isFetching.value = false;
  }
};

// Delete expense
const deleteExpense = async (expenseId) => {
  try {
    const response = await fetch(`${global.API_URL}/expenses/${expenseId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const data = await response.json();
    return data?.status || false;
  } catch (error) {
    console.error('Error deleting expense:', error);
    return false;
  }
};

// Handle delete item
const handleDeleteItem = (categoryIndex, itemIndex) => {
  const item = categories.value[categoryIndex].items[itemIndex];
  
  if (item.id) {
    conformationPopupIsOpen.value = true;
    deletionFunction.value = async () => {
      const success = await deleteExpense(item.id);
      if (success) {
        categories.value[categoryIndex].items.splice(itemIndex, 1);
      } else {
        console.error('Failed to delete expense');
      }
    };
  } else {
    categories.value[categoryIndex].items.splice(itemIndex, 1);
  }
};

// Handle confirm delete
const handleConfirmDelete = async () => {
  if (deletionFunction.value) {
    await deletionFunction.value();
    conformationPopupIsOpen.value = false;
    deletionFunction.value = null;
  }
};

// Handle data changes from BudgetTable for auto-save
const handleDataChanged = async (immediate = false) => {
  try {
    // Don't trigger auto-save if we're already saving
    if (isSaving.value) {
      return;
    }
    
    // Clear any existing validation error when user makes changes
    if (validationError.value) {
      validationError.value = '';
    }
    
    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    
    if (immediate) {
      // Save immediately
      try {
        await saveExpenses();
        tabValidationState.value[0] = true;
      } catch (error) {
        console.error('Immediate save failed:', error);
      }
    } else {
      // Set new timer for debounced save
      autoSaveTimer = setTimeout(async () => {
        try {
          // Trigger auto-save
          await saveExpenses();
          
          // Update validation state for expenses tab
          tabValidationState.value[0] = true;
        } catch (error) {
          console.error('Auto-save failed:', error);
          // Don't show error to user for auto-save failures to avoid interrupting their workflow
          // The error will be shown when they try to validate the step
        }
      }, AUTO_SAVE_DELAY);
    }
  } catch (error) {
    console.error('Error setting up auto-save:', error);
  }
};

// Add method to update item IDs without reordering
const updateItemIdsWithoutReordering = async () => {
  try {
    // Check if we have any items without IDs
    const hasNewItems = categories.value.some(category => 
      category.items.some(item => !item.id && item.name && item.name.trim() !== '')
    );
    
    if (!hasNewItems) {
      return; // No new items to update
    }
    
    // Fetch the latest expenses to get new IDs
    const response = await fetch(`${global.API_URL}/expenses?program_id=${programData.value.id}`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    
    // Create a map of expense descriptions to IDs for quick lookup
    const expenseMap = {};
    Object.values(data.expenses).flat().forEach(expense => {
      const key = `${expense.expense_type}|${expense.expense_description}`;
      expenseMap[key] = expense.id;
    });
    
    // Update IDs in categories without changing order
    let updatedCount = 0;
    categories.value.forEach(category => {
      category.items.forEach(item => {
        if (!item.id && item.name && item.name.trim() !== '') { // Only update items that don't have an ID yet
          // Use the save name (not display name) to match with API response
          const expenseTypeForSave = getExpenseTypeForSave(category.name);
          const key = `${expenseTypeForSave}|${item.name.trim()}`;
          if (expenseMap[key]) {
            item.id = expenseMap[key];
            updatedCount++;
          }
        }
      });
    });
    
    // Only update total budget if we actually updated some items
    if (updatedCount > 0) {
      total_programs_budget.value = data.expenses ? 
        Object.values(data.expenses).flat().reduce((sum, expense) => 
          sum + Number(expense.expense_cost), 0) : 0;
    }
  } catch (error) {
    console.error('Error updating item IDs:', error);
    // Don't throw error to avoid breaking the save flow
  }
};

// Add reference to questions component
const questionsComponent = ref(null);

// Add reference to budget justification component
const budgetJustificationComponent = ref(null);

// Modify saveExpenses to only save expenses
const saveExpenses = async () => {
  if (isSaving.value) {
    return lastSavePromise.value;
  }

  try {
    isSaving.value = true;
    
    // Filter out empty items (no name or all costs are 0)
    const expensesToSave = categories.value.flatMap(category => 
      category.items
        .filter(item => item.name && item.name.trim() !== '') // Only save items with names
        .map(item => ({
          id: item.id,
          expense_type: getExpenseTypeForSave(category.name), // Map display name to save name
          expense_description: item.name.trim(),
          expense_cost: item.year1.toString().replace(/,/g, ""),
          expense_cost_year_2: item.year2.toString().replace(/,/g, ""),
          expense_cost_year_3: item.year3.toString().replace(/,/g, ""),
          expenseable_type: "CoreProgram",
          expenseable_id: programData.value.id
        }))
    );

    // Don't save if no expenses to save
    if (expensesToSave.length === 0) {
      return { status: true, expenses: {} };
    }

    const response = await fetch(`${global.API_URL}/expenses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        expenses: expensesToSave,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save expenses');
    }

    const data = await response.json();
    
    // If save was successful and we have new items without IDs, update IDs without reordering
    if (data.status) {
      await updateItemIdsWithoutReordering();
    }
    
    // Emit event to notify that expenses have been updated
    window.dispatchEvent(new CustomEvent('expenses-updated', {
      detail: { 
        programId: programData.value.id,
        expenses: data.expenses || {}
      }
    }));
    
    return data;
  } catch (error) {
    console.error('Error saving expenses:', error);
    throw error;
  } finally {
    isSaving.value = false;
    lastSavePromise.value = null;
  }
};

// Add ref for error message
const errorMessageRef = ref(null);

// Add scroll to error method
const scrollToError = () => {
  if (errorMessageRef.value) {
    errorMessageRef.value.scrollIntoView({ 
      behavior: 'smooth',
      block: 'center'
    });
  }
};

// Add method to force immediate save
const forceSave = async () => {
  // Clear any pending auto-save
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
  
  try {
    await saveExpenses();
    tabValidationState.value[0] = true;
    return true;
  } catch (error) {
    console.error('Force save failed:', error);
    return false;
  }
};

// Modify validateStep function to handle tab-specific validation
const validateStep = async () => {
  try {
    // Clear previous validation error
    validationError.value = '';

    // Validate based on current tab
    switch (activeTab.value) {
      case 0: // Expenses tab
        if (categories.value.every(category => category.items.length === 0)) {
          validationError.value = "Please add data to the Expenses table.";
          tabValidationState.value[0] = false;
          await nextTick();
          scrollToError();
          return false;
        }
        // Save expenses when validating expenses tab
        try {
          const saveSuccess = await forceSave();
          if (!saveSuccess) {
            validationError.value = "Failed to save expenses. Please try again.";
            tabValidationState.value[0] = false;
            await nextTick();
            scrollToError();
            return false;
          }
        } catch (error) {
          console.error('Error saving expenses:', error);
          validationError.value = "Failed to save expenses. Please try again.";
          tabValidationState.value[0] = false;
          await nextTick();
          scrollToError();
          return false;
        }
        break;

      case 1: // Budget Justification tab
        if (budgetJustificationComponent.value) {
          const isValid = await budgetJustificationComponent.value.validate();
          tabValidationState.value[1] = isValid;
          if (!isValid) return false;
        }
        break;

      case 2: // Goals & Rationale tab
        if (questionsComponent.value) {
          const isValid = await questionsComponent.value.validate();
          tabValidationState.value[2] = isValid;
          if (!isValid) return false;
        }
        break;
    }

    // Check if all tabs are valid
    const allTabsValid = Object.values(tabValidationState.value).every(isValid => isValid);
    return allTabsValid;
  } catch (error) {
    console.error('Error validating step:', error);
    validationError.value = error.message || 'Failed to validate data. Please try again.';
    await nextTick();
    scrollToError();
    return false;
  }
};

// Add method to validate current tab
const validateCurrentTab = async () => {
  try {
    switch (activeTab.value) {
      case 0: // Expenses tab
        if (categories.value.every(category => category.items.length === 0)) {
          validationError.value = "Please add data to the Expenses table.";
          tabValidationState.value[0] = false;
          await nextTick();
          scrollToError();
          return false;
        }
        // Save expenses when moving from expenses tab
        try {
          const saveSuccess = await forceSave();
          if (!saveSuccess) {
            validationError.value = "Failed to save expenses. Please try again.";
            tabValidationState.value[0] = false;
            await nextTick();
            scrollToError();
            return false;
          }
          return true;
        } catch (error) {
          console.error('Error saving expenses:', error);
          validationError.value = "Failed to save expenses. Please try again.";
          tabValidationState.value[0] = false;
          await nextTick();
          scrollToError();
          return false;
        }

      case 1: // Budget Justification tab
        if (budgetJustificationComponent.value) {
          try {
            // First try to save the answers
            const saveResult = await budgetJustificationComponent.value.saveAnswers();
            if (!saveResult) {
              validationError.value = "Failed to save answers. Please try again.";
              tabValidationState.value[1] = false;
              return false;
            }
            
            // Then validate
            if (typeof budgetJustificationComponent.value.validate === 'function') {
              const isValid = await budgetJustificationComponent.value.validate();
              tabValidationState.value[1] = isValid;
              return isValid;
            }
            // If no validate method, assume valid
            tabValidationState.value[1] = true;
            return true;
          } catch (error) {
            console.error('Error saving/validating budget justification:', error);
            validationError.value = "Failed to save answers. Please try again.";
            tabValidationState.value[1] = false;
            return false;
          }
        }
        return true;

      case 2: // Goals & Rationale tab
        if (questionsComponent.value) {
          try {
            // First try to save the answers
            const saveResult = await questionsComponent.value.saveAnswers();
            if (!saveResult) {
              validationError.value = "Failed to save answers. Please try again.";
              tabValidationState.value[2] = false;
              return false;
            }
            
            // Then validate
            if (typeof questionsComponent.value.validate === 'function') {
              const isValid = await questionsComponent.value.validate();
              tabValidationState.value[2] = isValid;
              return isValid;
            }
            // If no validate method, assume valid
            tabValidationState.value[2] = true;
            return true;
          } catch (error) {
            console.error('Error saving/validating answers:', error);
            validationError.value = "Failed to save answers. Please try again.";
            tabValidationState.value[2] = false;
            return false;
          }
        }
        return true;
    }
  } catch (error) {
    console.error('Error validating current tab:', error);
    return false;
  }
};

// Use step validation
useStepValidation({
  initialValidState: false,
  validateFn: validateStep,
});

// Add watch for categories to clear validation error when data is added
watch(categories, (newCategories) => {
  if (validationError.value && !newCategories.every(category => category.items.length === 0)) {
    validationError.value = '';
  }
}, { deep: true });

// Initialize on mount
onMounted(async () => {
  try {
    isLoading.value = true;
    await fetchProgramData();
    await fetchExpenses();
  } catch (error) {
    console.error('Error initializing component:', error);
    error.value = 'Failed to load program data. Please try again.';
  } finally {
    isLoading.value = false;
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
});

// Add method to handle tab changes
const handleTabChange = async (newTabIndex) => {
  // If switching away from expenses tab, save current data
  if (activeTab.value === 0 && newTabIndex !== 0) {
    try {
      await forceSave();
    } catch (error) {
      console.error('Error saving expenses on tab change:', error);
    }
  }
  
  activeTab.value = newTabIndex;
};

// Update tabs state
const tabs = [
  { name: 'Expenses', component: 'BudgetTable' },
  { name: 'Budget Justification', component: 'CoreProgramBudgetJustification' },
  { name: 'Goals & Rationale', component: 'CoreProgramQuestions' }
];
const activeTab = ref(0);

// Expose methods and state to parent component
defineExpose({
  saveExpenses,
  forceSave,
  handleDeleteItem,
  activeTab,
  tabs,
  validateCurrentTab,
  tabValidationState
});
</script>

<style scoped>
.right-content-grid-vs {
  scrollbar-width: thin;
  scrollbar-color: #00423D #F5F6FA;
}

.right-content-grid-vs::-webkit-scrollbar {
  width: 8px;
}

.right-content-grid-vs::-webkit-scrollbar-track {
  background: #F5F6FA;
}

.right-content-grid-vs::-webkit-scrollbar-thumb {
  background-color: #00423D;
  border-radius: 4px;
}

/* Sticky header styles */
.sticky-header {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 20;
  padding-top: 1rem;
  margin-top: -1rem;
}

.sticky-tabs {
  position: sticky;
  top: 2.5rem; /* Adjust this value based on the height of program name */
  background-color: white;
  z-index: 10;
  padding-top: 1rem;
  margin-top: -1rem;
}

.tab-content {
  position: relative;
  z-index: 1;
}
</style> 